import React, { useEffect, useRef, useState, useMemo } from "react";
import { api, formatApiError, fmtTime, elapsedMin, elapsedLabel } from "@/lib/api";
import { useRealtimeEvents } from "@/hooks/useRealtimeEvents";
import { playKitchenBeep } from "@/lib/sound";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Flame, ChefHat, GlassWater, CookingPot, Clock, User, ShoppingBag, Globe, Sandwich, LayoutGrid } from "lucide-react";
import { toast } from "sonner";

// Configuración legacy para áreas del sistema. Para áreas custom (Parrilla, Barra…),
// se usa el fallback al final del archivo.
const AREA_CONFIG = {
  COCINA: { icon: ChefHat, label: "Cocina", chipBg: "bg-orange-100 dark:bg-orange-950/40", chipText: "text-orange-700 dark:text-orange-200", iconColor: "text-orange-500" },
  BEBIDAS: { icon: GlassWater, label: "Bebidas", chipBg: "bg-blue-100 dark:bg-blue-950/40", chipText: "text-blue-700 dark:text-blue-200", iconColor: "text-blue-500" },
  FREIDORA: { icon: CookingPot, label: "Freidora", chipBg: "bg-amber-100 dark:bg-amber-950/40", chipText: "text-amber-700 dark:text-amber-200", iconColor: "text-amber-500" },
};

const CUSTOM_AREA_CONFIGS = [
  { chipBg: "bg-emerald-100 dark:bg-emerald-950/40", chipText: "text-emerald-700 dark:text-emerald-200", iconColor: "text-emerald-500" },
  { chipBg: "bg-rose-100 dark:bg-rose-950/40", chipText: "text-rose-700 dark:text-rose-200", iconColor: "text-rose-500" },
  { chipBg: "bg-violet-100 dark:bg-violet-950/40", chipText: "text-violet-700 dark:text-violet-200", iconColor: "text-violet-500" },
  { chipBg: "bg-cyan-100 dark:bg-cyan-950/40", chipText: "text-cyan-700 dark:text-cyan-200", iconColor: "text-cyan-500" },
];

function getAreaCfg(areaName) {
  const upper = (areaName || "").toUpperCase();
  if (AREA_CONFIG[upper]) return AREA_CONFIG[upper];
  // Hash determinístico para mantener color estable por nombre
  let h = 0;
  for (const c of upper) h = (h * 31 + c.charCodeAt(0)) | 0;
  const cfg = CUSTOM_AREA_CONFIGS[Math.abs(h) % CUSTOM_AREA_CONFIGS.length];
  return { icon: Flame, label: areaName, ...cfg };
}

const TIPO_LABEL = {
  MESA: "MESA",
  LLEVAR: "PARA LLEVAR",
  PUBLICO: "PEDIDO WEB",
};

function timeColorClasses(mins) {
  if (mins <= 10) return { bg: "bg-emerald-500", text: "text-white", border: "border-emerald-500", ring: "ring-emerald-500/50" };
  if (mins <= 20) return { bg: "bg-amber-500", text: "text-white", border: "border-amber-500", ring: "ring-amber-500/50" };
  return { bg: "bg-red-600", text: "text-white", border: "border-red-600", ring: "ring-red-600/60 animate-pulse" };
}

function VariantesLine({ variantes }) {
  if (!variantes || variantes.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1 mt-1">
      {variantes.map((v, i) => (
        <span key={i} className="inline-flex items-center px-2 py-0.5 rounded bg-secondary text-foreground/90 text-xs font-semibold">
          {v.grupo}: <span className="ml-1 text-primary font-bold">{v.opcion}</span>
        </span>
      ))}
    </div>
  );
}

function RapidasLine({ rapidas }) {
  if (!rapidas || rapidas.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1 mt-1">
      {rapidas.map((r, i) => (
        <span key={i} className="inline-flex items-center px-2 py-0.5 rounded bg-amber-200 dark:bg-amber-900/70 text-amber-900 dark:text-amber-100 text-xs font-bold">
          ⚡ {r}
        </span>
      ))}
    </div>
  );
}

/**
 * KitchenPanel
 *  - Modo legacy: pasa prop `area` (string) → consulta /kitchen/{area}
 *  - Modo multi-área: NO pasar `area` → consulta /kitchen/mis-areas y muestra
 *    chips de filtro [Todos] [Freidora] [Bebidas] dinámicos.
 */
export default function KitchenPanel({ area }) {
  const isMulti = !area;
  const [ordenes, setOrdenes] = useState([]);
  const [misAreas, setMisAreas] = useState(area ? [area] : []);
  const [activeFilter, setActiveFilter] = useState("ALL"); // "ALL" o nombre del área
  const [loading, setLoading] = useState(true);
  // Set de IDs (item u orden) con request en vuelo — bloquea botones y evita
  // doble-click. Limpiamos sólo cuando el backend responde (ok o error).
  const [pendingIds, setPendingIds] = useState(() => new Set());
  const addPending = (id) => setPendingIds((s) => { const n = new Set(s); n.add(id); return n; });
  const removePending = (id) => setPendingIds((s) => { const n = new Set(s); n.delete(id); return n; });

  // Notificación sonora/visual al recibir un ITEM nuevo para el área activa.
  // Trackeamos `item_id` (no `orden_id`) para que el beep también suene cuando
  // se agreguen items a una orden ya conocida — comportamiento correcto en multi-área.
  const seenItemIds = useRef(new Set());

  // Tick global para refrescar el contador en vivo cada segundo.
  // Solo lo usamos para forzar re-render — el cálculo real vive en `elapsedLabel`.
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => (t + 1) % 1e9), 1000);
    return () => clearInterval(id);
  }, []);

  const load = async () => {
    try {
      let nextOrdenes;
      if (isMulti) {
        const r = await api.get(`/kitchen/mis-areas`);
        setMisAreas(r.data.areas || []);
        const sorted = (r.data.ordenes || []).sort((a, b) => {
          const t = (o) => Math.min(...(o.items_area || []).map((i) => new Date(i.enviado_en || o.created_at).getTime()));
          return t(a) - t(b);
        });
        nextOrdenes = sorted;
        setOrdenes(sorted);
      } else {
        const r = await api.get(`/kitchen/${area}`);
        const sorted = r.data.sort((a, b) => {
          const t = (o) => Math.min(...(o.items_area || []).map((i) => new Date(i.enviado_en || o.created_at).getTime()));
          return t(a) - t(b);
        });
        nextOrdenes = sorted;
        setOrdenes(sorted);
      }
      // Detectar items nuevos para alerta sonora/visual. Solo alertamos
      // después del primer load (`hydrated`) para no avisar al entrar al panel.
      // Se trackea por (orden_id, item_id) — un item nuevo en una orden conocida
      // también dispara la alerta (caso "se agregó otro Boneless").
      const seen = seenItemIds.current;
      const incomingItems = new Set();
      const nuevasOrdenes = new Map(); // orden_id -> orden, para mostrar el toast
      for (const o of (nextOrdenes || [])) {
        for (const it of (o.items_area || [])) {
          // Solo nos importan items activos para esta área (PENDIENTE / EN_PREPARACION).
          // No alertamos si ya están PREPARADO/CANCELADO/etc.
          if (!["PENDIENTE", "EN_PREPARACION"].includes(it.estado)) continue;
          const key = `${o.id}::${it.id}`;
          incomingItems.add(key);
          if (hydratedRef.current && !seen.has(key)) {
            nuevasOrdenes.set(o.id, o);
          }
        }
      }
      if (hydratedRef.current && nuevasOrdenes.size > 0) {
        playKitchenBeep();
        const arr = Array.from(nuevasOrdenes.values());
        toast.success(`Nueva comanda · ${arr[0].numero}${arr.length > 1 ? ` (+${arr.length - 1} más)` : ""}`, {
          duration: 5000,
        });
      }
      // Mantener el set con los items vigentes (no acumular para siempre).
      seenItemIds.current = incomingItems;
      hydratedRef.current = true;
    } catch (e) {
      // No pisar la lista local con [] en caso de error de red/timeout.
      // El polling 6s + SSE reintentan; el banner global muestra "Sin conexión".
      if (e?.response?.status !== 304) {
        // silent — se conserva la última lista válida
      }
    } finally { setLoading(false); }
  };

  // Bandera que asegura que el primer load no dispare beeps (la pantalla recién monta).
  const hydratedRef = useRef(false);

  useEffect(() => {
    load();
    const t = setInterval(load, 6000);
    return () => clearInterval(t);
  }, [area]);

  useRealtimeEvents(
    (msg) => {
      if (!msg?.type) return;
      if (msg.type.startsWith("orden.")) {
        load();
      }
    },
    () => load(),
  );

  const updateItem = async (ordenId, itemId, estado) => {
    if (pendingIds.has(itemId)) return; // doble-click guard
    addPending(itemId);
    try {
      // X-Request-Id: trazabilidad y futura idempotencia explícita.
      const requestId = (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`;
      await api.patch(
        `/ordenes/${ordenId}/items/${itemId}/estado`,
        { estado },
        { headers: { "X-Request-Id": requestId } },
      );
      toast.success("Actualizado");
      load(); // reconciliar con backend (fuente de verdad)
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      removePending(itemId);
    }
  };

  const marcarTodoPreparadoFiltrado = async (orden, itemsVisibles) => {
    const ordenKey = `orden:${orden.id}`;
    if (pendingIds.has(ordenKey)) return;
    addPending(ordenKey);
    try {
      const pendientes = itemsVisibles.filter((i) => i.estado !== "PREPARADO");
      // Procesamos secuencial — backend es idempotente por item: si reintenta el
      // mismo estado, no genera log duplicado (ver `update_item_estado`).
      for (const it of pendientes) {
        const requestId = (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`;
        await api.patch(
          `/ordenes/${orden.id}/items/${it.id}/estado`,
          { estado: "PREPARADO" },
          { headers: { "X-Request-Id": requestId } },
        );
      }
      toast.success(`Orden ${orden.numero}: ${pendientes.length} item(s) listos`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      removePending(ordenKey);
    }
  };

  // Filtrado por chip activo
  const ordenesVisibles = useMemo(() => {
    if (!isMulti || activeFilter === "ALL") return ordenes;
    const upper = activeFilter.toUpperCase();
    return ordenes
      .map((o) => ({ ...o, items_area: (o.items_area || []).filter((i) => (i.area || "").toUpperCase() === upper) }))
      .filter((o) => o.items_area.length > 0);
  }, [ordenes, activeFilter, isMulti]);

  // Encabezado: si es modo single, usa el config del área. Si es multi, usa "Mis pedidos".
  const headerCfg = !isMulti ? getAreaCfg(area) : { icon: LayoutGrid, label: "Mis pedidos", iconColor: "text-primary" };
  const HeaderIcon = headerCfg.icon;

  // Contadores por área (modo multi)
  const conteos = useMemo(() => {
    const map = {};
    ordenes.forEach((o) => {
      (o.items_area || []).forEach((it) => {
        const a = (it.area || "").toUpperCase();
        map[a] = (map[a] || 0) + 1;
      });
    });
    return map;
  }, [ordenes]);

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <div className={`flex h-12 w-12 sm:h-14 sm:w-14 items-center justify-center rounded-xl bg-secondary ${headerCfg.iconColor}`}>
            <HeaderIcon className="h-7 w-7 sm:h-8 sm:w-8" strokeWidth={2.5} />
          </div>
          <div>
            <h2 className="text-2xl sm:text-4xl font-black tracking-tight">{headerCfg.label}</h2>
            <p className="text-xs sm:text-sm text-muted-foreground mt-1">
              <span className="font-bold text-foreground">{ordenesVisibles.length}</span> orden(es) · actualización 6s
              {isMulti && misAreas.length > 0 && (
                <span className="ml-2 hidden sm:inline">· áreas: <span className="font-bold">{misAreas.join(", ")}</span></span>
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 text-[10px] sm:text-xs">
          <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-emerald-500" />0-10m</span>
          <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-amber-500" />11-20m</span>
          <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-red-600" />+20m</span>
        </div>
      </div>

      {/* Filtro de áreas (chips) — sólo en multi-área y si hay >= 2 áreas */}
      {isMulti && misAreas.length >= 2 && (
        <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1" data-testid="area-filter-chips">
          <button
            type="button"
            onClick={() => setActiveFilter("ALL")}
            className={`shrink-0 px-4 py-2 rounded-full text-sm font-bold border-2 transition-colors ${
              activeFilter === "ALL" ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card hover:border-primary/50"
            }`}
            data-testid="chip-todos"
          >
            Todos · {ordenes.reduce((s, o) => s + (o.items_area?.length || 0), 0)}
          </button>
          {misAreas.map((a) => {
            const cfg = getAreaCfg(a);
            const active = activeFilter === a;
            const count = conteos[a.toUpperCase()] || 0;
            return (
              <button
                key={a}
                type="button"
                onClick={() => setActiveFilter(a)}
                className={`shrink-0 px-4 py-2 rounded-full text-sm font-bold border-2 transition-colors inline-flex items-center gap-2 ${
                  active ? "border-primary bg-primary text-primary-foreground" : `border-border bg-card hover:border-primary/50 ${cfg.chipText}`
                }`}
                data-testid={`chip-${a.toLowerCase()}`}
              >
                <span>{cfg.label || a}</span>
                <span className={`text-xs px-1.5 py-0.5 rounded-full ${active ? "bg-primary-foreground/20" : "bg-secondary"}`}>{count}</span>
              </button>
            );
          })}
        </div>
      )}

      {loading ? (
        <Card><CardContent className="p-12 text-center text-muted-foreground">Cargando…</CardContent></Card>
      ) : ordenesVisibles.length === 0 ? (
        <Card>
          <CardContent className="p-8 sm:p-12 text-center">
            <Flame className="h-12 w-12 sm:h-16 sm:w-16 mx-auto text-muted-foreground/30" />
            <div className="mt-4 text-lg sm:text-xl font-bold">
              {isMulti && misAreas.length === 0
                ? "No tienes áreas asignadas"
                : `Sin pendientes${activeFilter !== "ALL" ? ` en ${activeFilter}` : ""}`}
            </div>
            <div className="text-xs sm:text-sm text-muted-foreground mt-1">
              {isMulti && misAreas.length === 0
                ? "Pide al administrador que te asigne una o más áreas de trabajo."
                : "Las nuevas órdenes aparecerán aquí en tiempo real."}
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3 sm:gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
          {ordenesVisibles.map((o) => {
            const itemsConTiempo = (o.items_area || []).map((it) => ({
              ...it,
              _mins: elapsedMin(it.enviado_en || o.created_at),
            }));
            const masAntiguo = itemsConTiempo.reduce((acc, it) => (it._mins > acc ? it._mins : acc), 0);
            const tc = timeColorClasses(masAntiguo);
            const tipoLabel = TIPO_LABEL[o.tipo] || o.tipo;
            const TipoIcon = o.tipo === "MESA" ? Sandwich : (o.tipo === "PUBLICO" ? Globe : ShoppingBag);
            const todosPreparados = itemsConTiempo.every((i) => i.estado === "PREPARADO");
            return (
              <Card key={o.id} className={`border-t-8 ${tc.border} ring-2 ${tc.ring} shadow-lg`} data-testid={`kitchen-orden-${o.id}`}>
                <CardContent className="p-3 sm:p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="font-mono text-xl sm:text-2xl font-black leading-none tracking-tight">{o.numero}</div>
                      <div className="mt-1 flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-muted-foreground">
                        <TipoIcon className="h-3 w-3" /> {tipoLabel}
                      </div>
                    </div>
                    <div className={`px-2.5 py-1.5 rounded-md ${tc.bg} ${tc.text} font-black text-xl sm:text-2xl tabular-nums whitespace-nowrap inline-flex items-center gap-1`} data-testid={`orden-tiempo-${o.id}`}>
                      <Clock className="h-4 w-4" /> {elapsedLabel(itemsConTiempo.reduce((min, it) => {
                        const t = it.enviado_en || o.created_at;
                        return !min || new Date(t) < new Date(min) ? t : min;
                      }, null))}
                    </div>
                  </div>

                  <div className="mt-3 py-2 sm:py-3 border-y border-border">
                    {o.tipo === "MESA" && o.mesa_nombre ? (
                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Mesa</div>
                        <div className="text-3xl sm:text-4xl font-black tracking-tight">{o.mesa_nombre}</div>
                      </div>
                    ) : (
                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Cliente</div>
                        <div className="text-lg sm:text-xl font-black truncate">{o.cliente_nombre || "—"}</div>
                        {o.cliente_telefono && <div className="text-xs text-muted-foreground">{o.cliente_telefono}</div>}
                      </div>
                    )}
                    <div className="mt-1 text-xs text-muted-foreground flex items-center gap-1">
                      <User className="h-3 w-3" /> {o.created_by_nombre || "—"} · {fmtTime(o.created_at)}
                    </div>
                  </div>

                  {o.observaciones && (
                    <div className="mt-3 px-3 py-2 rounded bg-amber-100 dark:bg-amber-900/40 text-amber-900 dark:text-amber-100 text-xs font-semibold">
                      📌 Orden: {o.observaciones}
                    </div>
                  )}

                  <div className="mt-3 space-y-2">
                    {itemsConTiempo.map((it) => {
                      const itc = timeColorClasses(it._mins);
                      const areaCfg = getAreaCfg(it.area);
                      const AreaIcon = areaCfg.icon;
                      return (
                      <div key={it.id} className="rounded-lg bg-secondary/60 p-3 border border-border" data-testid={`item-row-${it.id}`}>
                        {/* Etiqueta de área SIEMPRE visible para diferenciar */}
                        <div className="flex items-center justify-between gap-2 mb-2">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-black uppercase tracking-wider ${areaCfg.chipBg} ${areaCfg.chipText}`} data-testid={`item-area-${it.id}`}>
                              <AreaIcon className="h-3 w-3" />{(areaCfg.label || it.area)}
                            </span>
                            {it.es_acompanamiento && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-black uppercase tracking-wider bg-amber-200 dark:bg-amber-900/70 text-amber-900 dark:text-amber-100" data-testid={`item-acomp-${it.id}`}>
                                🍟 PORCIÓN
                              </span>
                            )}
                            {it.es_ingrediente_externo && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-black uppercase tracking-wider bg-indigo-200 dark:bg-indigo-900/70 text-indigo-900 dark:text-indigo-100" data-testid={`item-ingrediente-${it.id}`}>
                                🔗 INGREDIENTE
                              </span>
                            )}
                          </div>
                          <div className={`shrink-0 px-2 py-0.5 rounded-md ${itc.bg} ${itc.text} font-black text-xs tabular-nums inline-flex items-center gap-1`} title="Tiempo desde envío">
                            <Clock className="h-3 w-3" /> {elapsedLabel(it.enviado_en || o.created_at)}
                          </div>
                        </div>
                        <div className="text-lg sm:text-xl font-black leading-tight">
                          <span className="text-primary mr-1">{it.cantidad}×</span>
                          {it.nombre}
                        </div>
                        {it.es_acompanamiento && it.parent_producto_nombre && (
                          <div className="mt-1 text-xs">
                            <span className="text-muted-foreground">Acompaña a:</span>{" "}
                            <span className="font-bold text-foreground">{it.parent_producto_nombre}</span>
                          </div>
                        )}
                        {it.es_ingrediente_externo && it.parent_producto_nombre && (
                          <div className="mt-1 text-xs" data-testid={`item-ingrediente-parent-${it.id}`}>
                            <span className="text-muted-foreground">Pertenece a:</span>{" "}
                            <span className="font-bold text-foreground">{it.parent_producto_nombre}</span>
                          </div>
                        )}
                        <VariantesLine variantes={it.variantes} />
                        <RapidasLine rapidas={it.variantes_rapidas} />
                        {it.observaciones && (
                          <div className="mt-2 text-xs px-2 py-1 rounded bg-orange-100 dark:bg-orange-950/50 text-orange-900 dark:text-orange-200 font-semibold">
                            📝 {it.observaciones}
                          </div>
                        )}
                        <div className="mt-3 flex gap-2">
                          {it.estado === "PENDIENTE" && (
                            <Button className="flex-1 h-11 sm:h-12 font-bold" variant="outline" disabled={pendingIds.has(it.id)} onClick={() => updateItem(o.id, it.id, "EN_PREPARACION")} data-testid={`prep-${it.id}`}>
                              {pendingIds.has(it.id) ? "…" : "Iniciar"}
                            </Button>
                          )}
                          {it.estado === "EN_PREPARACION" && (
                            <Button className="flex-1 h-11 sm:h-12 font-bold" disabled={pendingIds.has(it.id)} onClick={() => updateItem(o.id, it.id, "PREPARADO")} data-testid={`done-${it.id}`}>
                              {pendingIds.has(it.id) ? "Guardando…" : "✓ Listo"}
                            </Button>
                          )}
                          {it.estado === "PREPARADO" && (
                            <div className="flex-1 h-11 sm:h-12 inline-flex items-center justify-center rounded-md bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-200 font-bold">
                              ✓ Preparado
                            </div>
                          )}
                        </div>
                      </div>
                      );
                    })}
                  </div>

                  {itemsConTiempo.length > 1 && !todosPreparados && (
                    <Button className="w-full h-11 sm:h-12 mt-3 font-black" disabled={pendingIds.has(`orden:${o.id}`)} onClick={() => marcarTodoPreparadoFiltrado(o, itemsConTiempo)} data-testid={`all-done-${o.id}`}>
                      {pendingIds.has(`orden:${o.id}`) ? "Guardando…" : "✓ Marcar TODO preparado"}
                    </Button>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
